import { fetchWithTokenAndRegion } from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import { getOrCreateGlobalStore } from '@adsk/alloy-react-context';

import { CurrentAccount } from '../CurrentAccountEntitlementsContext';
import {
  readCachedDataByUrl,
  writeCachedDataByUrl,
} from '../helpers/cacheHelpers';
import { BaseDataStoreError } from '../helpers/createDataModificationStore';

export type CurrentAccountEntitlementsStore = {
  error: BaseDataStoreError | null;
  loading: boolean;
  _started: boolean;
  url: string | null;
  currentAccount?: CurrentAccount;
  setCurrentAccount: (currentAccount?: CurrentAccount) => void;

  // Fetch methods
  fetchData: (url?: string | null) => Promise<void>;
  retry: () => Promise<void>;
};

const defaultFetchCurrentAccountEntitlements = (url: string) =>
  fetchWithTokenAndRegion<CurrentAccount>(url);

export const useCurrentAccountEntitlementsStore = (
  contextName: string = CONTEXT_NAMES.CurrentAccountEntitlementsContext,
  fetchFunction: (
    url: string,
  ) => Promise<CurrentAccount> = defaultFetchCurrentAccountEntitlements,
) =>
  getOrCreateGlobalStore<CurrentAccountEntitlementsStore>(
    (set, get) => ({
      error: null,
      loading: true,
      url: null,
      currentAccount: undefined,
      _started: false,
      setCurrentAccount: (nextAccount?: CurrentAccount) => {
        const { url } = get();
        set({ currentAccount: nextAccount });

        // Update cache with new account data if provided
        if (nextAccount && url) {
          writeCachedDataByUrl(contextName, url, nextAccount);
        }
      },

      fetchData: async (url) => {
        if (!url) {
          return;
        }

        // Set _started immediately to prevent race conditions
        // Check must happen before async operation begins
        const currentState = get();
        if (currentState._started && currentState.url === url) {
          return;
        }

        // Set loading state and guard flag atomically
        set({
          _started: true,
          loading: true,
          error: null,
          url,
        });

        // Load cached data immediately before fetching (only if not already loading)
        // This provides instant UI feedback while fresh data loads
        const cachedData = readCachedDataByUrl<CurrentAccount>(
          contextName,
          url,
        );
        if (cachedData) {
          set({ currentAccount: cachedData });
        }

        try {
          const response = await fetchFunction(url);

          // Write to cache after successful fetch
          writeCachedDataByUrl(contextName, url, response);

          set({
            error: null,
            loading: false,
            currentAccount: response,
          });
        } catch (error) {
          set({
            error: error as Error,
            loading: false,
          });
        }
      },

      retry: async () => {
        const { fetchData, url } = get();
        set({ _started: false });
        await fetchData(url);
      },
    }),
    contextName,
  )();
